import type { FreeTextResult } from "./api";
import type { ThreadMessage } from "./types";

/**
 * What to show for one answered ask, and what to log about it.
 *
 * This is the whole "which outcome did we get" decision, lifted out of the
 * overlay. It is pure — an answer in, messages out — so each branch can be
 * asserted directly instead of by driving a component through a mocked fetch
 * and reading the DOM.
 */

/** The messages to append once an ask resolves. */
export function messagesForResult(
    result: FreeTextResult,
    question: string,
): ThreadMessage[] {
    // Unroutable input: neither a training request nor a support question.
    // Offer a human rather than a dead end.
    if (result.kind === "not_workout_request") {
        return [
            {
                kind: "support",
                origin: "unroutable",
                question,
                // Only offer support help to users who can actually reach it.
                // Outside the classifier rollout the server never routes to
                // articles, so the wider promise would send them rephrasing
                // towards a dead end.
                content: result.helpAvailable
                    ? "I can help with your training or with using Volley — try rephrasing your question."
                    : "I can only help with workout recommendations right now — try asking about a skill, drill, or workout you'd like to train.",
            },
        ];
    }

    // The request itself failed, so we never learned the intent. Say so and
    // offer a human: rendering the recommendation miss copy here would tell
    // someone who asked a support question to rephrase, and never admit
    // anything broke.
    if (result.kind === "error") {
        return [
            {
                kind: "support",
                origin: "error",
                question,
                content:
                    "Something went wrong on my end — try asking again in a moment.",
            },
        ];
    }

    const { cards, intent, summary } = result;

    if (cards.length === 0) {
        // A support question we have no article for is the one miss worth
        // escalating: the user asked something answerable, we just don't hold
        // the answer.
        return intent === "HELP"
            ? [
                  {
                      kind: "support",
                      origin: "help",
                      question,
                      content: "I couldn't find an article on that.",
                  },
              ]
            : [
                  {
                      kind: "assistant",
                      content:
                          "I couldn't find a match for that right now — try asking another way.",
                  },
              ];
    }

    // A help answer leads with the summary when we have one: it answers the
    // question in the thread, which is the whole point of asking here rather
    // than opening the help centre. The generic lead-in is then dropped rather
    // than stacked on top — "here's what I found" reads as filler once a real
    // answer sits directly above the same cards.
    const lead: ThreadMessage =
        intent === "HELP" && summary
            ? { kind: "assistant", content: summary }
            : {
                  kind: "assistant",
                  content:
                      intent === "HELP"
                          ? "Here's what I found that should help:"
                          : "Here's what I'd recommend:",
              };

    // Only an answer we actually stand behind gets rated. The misses above
    // already say we could not help and offer a way on, so asking "did this
    // help?" under one would be asking a question we just answered ourselves.
    //
    // No askId means the ask log write failed. The answer still stands; it just
    // has nowhere to record a rating, so none is offered.
    return [
        lead,
        { kind: "carousel", cards, intent },
        ...(result.askId === undefined
            ? []
            : [
                  {
                      kind: "rating" as const,
                      askId: result.askId,
                      intent,
                      question,
                  },
              ]),
    ];
}

/** The funnel metadata for one answered ask. */
export function resultEventMetadata(
    result: FreeTextResult,
): Record<string, unknown> {
    if (result.kind === "not_workout_request") {
        return {
            action: "free_text_result",
            outcome: "not_workout_request",
            intent: "UNCLASSIFIED",
            count: 0,
        };
    }
    if (result.kind === "error") {
        return { action: "free_text_result", outcome: "error", count: 0 };
    }
    return {
        action: "free_text_result",
        intent: result.intent,
        count: result.cards.length,
        contentIds: result.cards.map((c) => c.id),
    };
}
